import {
  TrainingInfoSentences,
  TrainingInfoSentence,
} from '../../types/apis/trainingInfoApi';

import { floorNumber, numberOfDecimalPlaces } from '../numberConverter';

/**
 * 再生済みではないセンテンスか
 * @param sentence センテンス
 * @param currentTime 現在時刻
 * @returns true:再生前 or 再生中 / false 再生済み
 */
const isNotPlayedSentence = (
  sentence: TrainingInfoSentence,
  currentTime: number,
): boolean => {
  // start - end間
  if (sentence.start <= currentTime && currentTime <= sentence.end) {
    return true;
  }

  // startより前（センテンス間の場合)
  if (currentTime < sentence.start) {
    return true;
  }

  return false;
};

/**
 * 現在時刻からセンテンスIDを返す
 * @param currentTime 現在時刻
 * @param sentenceData センテンスの配列
 * @returns センテンスID（空配列：空文字 / 見つからなければ、先頭のセンテンスID）
 */
const sentenceIdFromCurrentTime = (
  currentTime: number,
  sentenceData: TrainingInfoSentences[],
): string => {
  // 空配列
  if (sentenceData === null || sentenceData.length < 1) {
    return '';
  }
  for (let i = 0; i < sentenceData.length; i += 1) {
    const { english, japanese } = sentenceData[i];

    // 英語
    if (isNotPlayedSentence(english, currentTime)) {
      return english.id;
    }

    // 日本語
    if (japanese && isNotPlayedSentence(japanese, currentTime)) {
      return japanese.id;
    }
  }

  // 見つからない場合は、先頭のsentence - id返す
  return sentenceData[0].english.id;
};

/**
 * センテンスIDからスタート時間を返す
 * @param sentenceId
 * @param sentences
 * @returns 再生時間
 */
const startTimeFromSentenceId = (
  sentenceId: string,
  sentenceData: TrainingInfoSentences[],
): number => {
  for (let i = 0; i < sentenceData.length; i += 1) {
    const { english, japanese } = sentenceData[i];
    // 英語
    if (english.id === sentenceId) {
      return english.start;
    }

    // 日本語
    if (japanese && japanese.id === sentenceId) {
      return japanese.start;
    }
  }

  return 0;
};

/**
 次のセンテンスの開始時間を返す
 @param currentTime 再生時間
 @param sentenceData センテンス一覧
 @return 次のセンテンスの開始時間
 */
const nextSentenceStartTime = (
  currentTime: number,
  sentenceData: TrainingInfoSentences[],
): number => {
  // 再生中のセンテンスを発見済みか
  let isFinded = false;
  for (let i = 0; i < sentenceData.length; i += 1) {
    const { english, japanese } = sentenceData[i];

    // 配列に直して、japaneseがnullなら除外
    const sentences = [english, japanese].filter(
      (x) => !(x === null || x === undefined),
    ) as TrainingInfoSentence[];

    for (let j = 0; j < sentences.length; j += 1) {
      const sentence = sentences[j];

      // start・end・currentTimeの中で、小数点の桁数が一番小さいもの取得
      // 最小のものから、１つ少ない桁で調べる
      let minDigits = Math.min(
        numberOfDecimalPlaces(sentence.start),
        numberOfDecimalPlaces(currentTime),
      );
      minDigits = Math.min(minDigits, numberOfDecimalPlaces(sentence.end));
      minDigits = minDigits <= 0 ? minDigits : minDigits - 1;

      const floorStartTime = floorNumber(sentence.start, -minDigits);
      const floorEndTime = floorNumber(sentence.end, -minDigits);
      const floorCurrentTime = floorNumber(currentTime, -minDigits);

      // 見つかっているかつスタート前なら、現在のループのものを返す
      if (isFinded && floorCurrentTime < floorStartTime) {
        return sentence.start;
      }

      // start - end間
      if (
        floorStartTime <= floorCurrentTime &&
        floorCurrentTime <= floorEndTime
      ) {
        isFinded = true;
      }

      // startより前(センテンス間)の場合、現在のループのものを返す
      if (floorCurrentTime < floorStartTime) {
        return sentence.start;
      }
    }
  }

  // 最後のセンテンス再生後、または最後のセンテンス再生中の場合は、0を返す。
  return 0;
};

/**
 前のセンテンスの開始時間を返す
 @param currentTime 再生時間
 @param sentenceData センテンス一覧 
 @returns 前のセンテンスの再生時間 
 */
const prevSentenceCurrentTime = (
  currentTime: number,
  sentenceData: TrainingInfoSentences[],
): number => {
  // 再生中のセンテンスを発見済みか
  let isFinded = false;

  for (let i = sentenceData.length - 1; i >= 0; i -= 1) {
    const { english, japanese } = sentenceData[i];

    // 配列に直して、japaneseがnullなら除外
    const sentences = [japanese, english].filter(
      (x) => !(x === null || x === undefined),
    ) as TrainingInfoSentence[];

    for (let j = 0; j < sentences.length; j += 1) {
      const sentence = sentences[j];
      // 見つかっていれば、現在のループのものを返す
      if (isFinded) {
        return sentence.start;
      }

      // start - end間
      if (sentence.start <= currentTime && currentTime <= sentence.end) {
        isFinded = true;
      }

      // endより後(センテンス間)の場合、現在のループのものを返す
      if (sentence.end < currentTime) {
        return sentence.start;
      }
    }
  }

  // 最初のセンテンス再生前、または最初のセンテンス再生中の場合は、0を返す。
  return 0;
};

export {
  sentenceIdFromCurrentTime,
  startTimeFromSentenceId,
  prevSentenceCurrentTime,
  nextSentenceStartTime,
};
